import { z } from 'zod';
import { idSchema, instantSchema } from './common.js';
import { recordEntitySchema } from './notes-tasks.js';
/**
 * The activity ledger (RELAY-946): one row per user-visible change to a record, written in the
 * SAME transaction as the change, so the timeline is never behind the data. The API reads a
 * record's timeline as one indexed query; nothing is merged on the client.
 */
export const ACTIVITY_TYPES = [
    'created',
    'field_changed',
    'stage_changed',
    'deleted',
    'restored',
    'merged',
    'contact_added',
    'contact_removed',
    'deal_added',
    // A bundle added to or removed from a deal (TS-925 follow-up).
    'bundle_added',
    'bundle_removed',
    'pipeline_added',
    'pipeline_removed',
    'pipeline_archived',
    'pipeline_restored',
    'shortlisted',
    'unshortlisted',
    'note_added',
    'task_created',
    'task_completed',
    'task_reopened',
    // To do ↔ In progress (TS-1739); moves into and out of Done are completed / reopened.
    'task_status_changed',
    'task_reassigned',
    'document_added',
    // Messages ingested from other systems (TS-955): Relay's Gmail, Confirmly's WhatsApp.
    'email_sent',
    'email_received',
    'message_sent',
    'message_received',
];
export const activityTypeSchema = z.enum(ACTIVITY_TYPES);
/** Who made the change: a person in the app, the platform itself (a job), or an integration. */
export const ACTIVITY_SOURCES = ['user', 'system', 'integration'];
export const activitySourceSchema = z.enum(ACTIVITY_SOURCES);
/** The Activity tab's filter chips: each is a set of types, so a filter stays one query. */
export const ACTIVITY_CATEGORIES = {
    notes: ['note_added'],
    tasks: [
        'task_created',
        'task_completed',
        'task_reopened',
        'task_status_changed',
        'task_reassigned',
    ],
    changes: [
        'created',
        'field_changed',
        'stage_changed',
        'deleted',
        'restored',
        'merged',
        // A project's line items (RELAY-949): contacts joining or leaving, a deal added under it.
        'contact_added',
        'contact_removed',
        'deal_added',
        'bundle_added',
        'bundle_removed',
        // A project joining or leaving a pipeline (TS-1747), or a lost place archived and restored.
        'pipeline_added',
        'pipeline_removed',
        'pipeline_archived',
        'pipeline_restored',
        // A candidate put on, or taken off, a job's shortlist (RELAY-951).
        'shortlisted',
        'unshortlisted',
    ],
    documents: ['document_added'],
    messages: ['email_sent', 'email_received', 'message_sent', 'message_received'],
};
export const ACTIVITY_CATEGORY_NAMES = Object.keys(ACTIVITY_CATEGORIES);
/** Every type belongs to exactly one category (asserted in the tests). */
export function categoryOf(type) {
    for (const name of ACTIVITY_CATEGORY_NAMES)
        if (ACTIVITY_CATEGORIES[name].includes(type))
            return name;
    throw new Error(`activity type ${type} has no category`);
}
/** What the change was about, when it was a thing of its own: the note, task or document. */
export const activityRefSchema = z.object({
    type: z.enum(['notes', 'tasks', 'documents']),
    id: idSchema,
});
export const activitySchema = z.object({
    id: idSchema,
    entityType: recordEntitySchema,
    entityId: idSchema,
    type: activityTypeSchema,
    source: activitySourceSchema,
    actor: z.object({ id: idSchema, name: z.string(), email: z.string() }).nullable(),
    occurredAt: instantSchema,
    summary: z.string(),
    /** Type-specific detail: `{ changes: { field: { old, new } } }`, `{ from, to }`, `{ noteId }`… */
    payload: z.record(z.string(), z.unknown()).nullable(),
    ref: activityRefSchema.nullable(),
});
/** `?types=note_added,task_created&cursor=…&limit=50`; no `types` = everything. */
export const activityQuerySchema = z.object({
    types: z
        .string()
        .optional()
        .transform((s) => s
        ? s
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean)
        : undefined)
        .pipe(z.array(activityTypeSchema).min(1).max(ACTIVITY_TYPES.length).optional()),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
//# sourceMappingURL=activities.js.map